import React, {useEffect, useMemo, useRef, useState} from 'react';

export interface WarehouseSummaryRow {
    warehouse_id?: string;
    warehouse_name?: string;
    stock_quantity?: number | string;
    address?: string;
    city?: string;
    state?: string;
    postcode?: string;
    phone?: string;
    is_assigned?: boolean;
}

export interface WarehouseSummaryData {
    scope?: string;
    assigned_warehouse_id?: string;
    total_warehouses?: number;
    total_stock?: number;
    rows?: WarehouseSummaryRow[];
}

interface NormalizedWarehouseSummaryRow {
    warehouseId: string;
    warehouseName: string;
    stockQuantity: number;
    isAssigned: boolean;
}

interface NormalizedWarehouseSummaryData {
    scope: 'all_warehouses' | 'assigned_only';
    assignedWarehouseId: string;
    totalWarehouses: number;
    totalStock: number;
    rows: NormalizedWarehouseSummaryRow[];
}

export interface WarehouseSummaryProps {
    summary?: WarehouseSummaryData | null;
    displayMode?: string;
    title?: string;
    triggerLabel?: string;
    triggerClassName?: string;
    className?: string;
}

const normalizeDisplayMode = (value: unknown): 'off' | 'table' | 'popover' | 'modal' => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'table' || normalized === 'popover' || normalized === 'modal') {
        return normalized;
    }

    return 'off';
};

const toSafeNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const normalizeRow = (raw: WarehouseSummaryRow | null | undefined): NormalizedWarehouseSummaryRow | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const warehouseId = String(raw.warehouse_id || '').trim();
    const warehouseName = String(raw.warehouse_name || warehouseId || 'Warehouse').trim();
    if (warehouseName === '') {
        return null;
    }

    return {
        warehouseId,
        warehouseName,
        stockQuantity: toSafeNumber(raw.stock_quantity),
        isAssigned: raw.is_assigned === true,
    };
};

const normalizeSummary = (raw: WarehouseSummaryData | null | undefined): NormalizedWarehouseSummaryData | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const rows = Array.isArray(raw.rows)
        ? raw.rows
            .map((row) => normalizeRow(row))
            .filter((row): row is NormalizedWarehouseSummaryRow => row !== null)
        : [];

    if (rows.length === 0) {
        return null;
    }

    const assignedWarehouseId = String(raw.assigned_warehouse_id || '').trim();
    const totalStock = rows.reduce((sum, row) => sum + row.stockQuantity, 0);
    const totalWarehouses = rows.length;
    const scope = String(raw.scope || '').trim().toLowerCase() === 'assigned_only'
        ? 'assigned_only'
        : 'all_warehouses';

    return {
        scope,
        assignedWarehouseId,
        totalWarehouses,
        totalStock: Math.max(totalStock, toSafeNumber(raw.total_stock)),
        rows,
    };
};

const buildSummaryMeta = (summary: NormalizedWarehouseSummaryData): string => {
    const warehouseLabel = summary.totalWarehouses === 1 ? 'warehouse' : 'warehouses';
    if (summary.scope === 'assigned_only' && summary.assignedWarehouseId !== '') {
        return `Assigned warehouse only · ${summary.totalStock} available`;
    }

    return `${summary.totalWarehouses} ${warehouseLabel} · ${summary.totalStock} available`;
};

const renderPanel = (
    summary: NormalizedWarehouseSummaryData,
    title: string,
    dismiss: (() => void) | null
): React.ReactElement => {
    return (
        <div className="warehouse-summary__panel">
            <div className="warehouse-summary__header">
                <div className="warehouse-summary__header-copy">
                    <div className="warehouse-summary__title" role="heading" aria-level={4}>
                        {title}
                    </div>
                    <p className="warehouse-summary__meta">{buildSummaryMeta(summary)}</p>
                </div>
                {dismiss && (
                    <button
                        type="button"
                        className="warehouse-summary__close"
                        onClick={dismiss}
                        aria-label="Close warehouse summary"
                    >
                        <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                )}
            </div>
            <div className="warehouse-summary__table-wrap">
                <table className="warehouse-summary__table">
                    <thead>
                    <tr>
                        <th>Warehouse</th>
                        <th>Available</th>
                    </tr>
                    </thead>
                    <tbody>
                    {summary.rows.map((row) => {
                        return (
                            <tr key={`${row.warehouseId || row.warehouseName}-${row.stockQuantity}`}>
                                <td>
                                    <div className="warehouse-summary__warehouse-name">
                                        <strong>{row.warehouseName}</strong>
                                        {row.isAssigned && (
                                            <span className="warehouse-summary__badge">Assigned</span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <span className={`warehouse-summary__stock-pill ${row.stockQuantity > 0 ? 'is-available' : 'is-unavailable'}`}>
                                        {row.stockQuantity > 0 ? `${row.stockQuantity} in stock` : 'Out of stock'}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const WarehouseSummary: React.FC<WarehouseSummaryProps> = ({
    summary,
    displayMode,
    title = 'Warehouse availability',
    triggerLabel = 'Warehouse stock',
    triggerClassName = '',
    className = '',
}) => {
    const normalizedSummary = useMemo(() => normalizeSummary(summary), [summary]);
    const normalizedDisplayMode = normalizeDisplayMode(displayMode);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setIsOpen(false);
    }, [normalizedDisplayMode, normalizedSummary?.rows.length, title, triggerLabel]);

    useEffect(() => {
        if (!isOpen || normalizedDisplayMode !== 'popover') {
            return;
        }

        const handlePointerDown = (event: MouseEvent) => {
            if (!wrapperRef.current) {
                return;
            }

            if (!wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, normalizedDisplayMode]);

    useEffect(() => {
        if (!isOpen || normalizedDisplayMode !== 'modal') {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, normalizedDisplayMode]);

    if (!normalizedSummary || normalizedDisplayMode === 'off') {
        return null;
    }

    if (normalizedDisplayMode === 'table') {
        return (
            <div className={`warehouse-summary warehouse-summary--table ${className}`.trim()}>
                {renderPanel(normalizedSummary, title, null)}
            </div>
        );
    }

    return (
        <div
            ref={wrapperRef}
            className={`warehouse-summary warehouse-summary--${normalizedDisplayMode} ${className}`.trim()}
        >
            <button
                type="button"
                className={`warehouse-summary__trigger ${triggerClassName}`.trim()}
                onClick={() => setIsOpen((previousState) => !previousState)}
                aria-expanded={isOpen}
            >
                <span>{triggerLabel}</span>
                <span className="warehouse-summary__trigger-count">
                    {normalizedSummary.totalWarehouses}
                </span>
            </button>

            {normalizedDisplayMode === 'popover' && isOpen && (
                <div className="warehouse-summary__popover" role="dialog" aria-modal="false">
                    {renderPanel(normalizedSummary, title, () => setIsOpen(false))}
                </div>
            )}

            {normalizedDisplayMode === 'modal' && isOpen && (
                <div
                    className="warehouse-summary__modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setIsOpen(false)}
                >
                    <div
                        className="warehouse-summary__modal"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {renderPanel(normalizedSummary, title, () => setIsOpen(false))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WarehouseSummary;
